import {
  AircraftCategory,
  NoiseEventSummaryFilter,
  OperatorCategory,
} from 'src/app/gql/__generated__/hooks';
import { FilterKeyConfig } from 'src/utils/filters/parseUrlFilters';

import { IFilterTypes, IRangeFilters } from 'src/@noiseEvents/props';
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'src/utils';

export const filterKeyMap: FilterKeyConfig[] = [
  // Strings
  { key: 'locationNames', type: 'string', isArray: true },
  { key: 'aircraftCategories', type: 'string', isArray: true },
  { key: 'aircraftTypes', type: 'string', isArray: true },
  { key: 'airlines', type: 'string', isArray: true },
  { key: 'operatorCategories', type: 'string', isArray: true },
  { key: 'operationIds', type: 'number', isArray: true },
  { key: 'limitToIds', type: 'number', isArray: true }, // Noise event ids
  { key: 'classification', type: 'string', displayName: 'cause', isArray: true },
  // Numbers
  { key: 'sel', type: 'numberRange', displayName: 'selRange', isArray: true },
  { key: 'maxLevel', type: 'numberRange', displayName: 'lmaxRange', isArray: true },
  { key: 'duration', type: 'numberRange', isArray: true },
  // Dates
  { key: 'timePeriod', type: 'timeRange', isArray: false },
];

export const urlToNoiseEventFilterState = (
  input: NoiseEventSummaryFilter
): {
  time: IRangeFilters;
  tableFilters: IFilterTypes;
  maxLevel: IRangeFilters;
  sel: IRangeFilters;
  duration: IRangeFilters;
} => {
  const tableFilters: IFilterTypes = {
    locationNames: [],
    aircraftCategories: [],
    operatorCategories: [],
    classifications: [],
  };
  const time = { from: '', to: '' };
  const maxLevel = { from: '', to: '' };
  const sel = { from: '', to: '' };
  const duration = { from: '', to: '' };

  Object.entries(input).forEach(([key, value]) => {
    const tempValue = value as string[];

    if (key === 'operatorCategories') {
      tableFilters.operatorCategories = tempValue.map((category: string) => ({
        key: category,
        label: category,
        icon: `ac-${category.toLowerCase()}`,
        className: 'filter-icon-ac',
      }));
    } else if (key === 'locationNames') {
      tableFilters.locationNames = tempValue.map((location: string) => ({
        key: location,
        label: location,
        icon: '',
        className: '',
      }));
    } else if (key === 'classification') {
      tableFilters.classifications = tempValue.map((category: string) => ({
        key: category === 'Empty' ? 'NotSet' : category,
        label: category,
        icon: undefined,
        className: '',
      }));
    } else if (key === 'timePeriod') {
      const arr = value as Record<string, number>;
      if (arr && typeof arr.start === 'number' && typeof arr.end === 'number') {
        time.from = convertSecondsToTimeString(arr.start);
        time.to = convertSecondsToTimeString(arr.end);
      }
    } else if (
      (key === 'sel' || key === 'maxLevel' || key === 'duration') &&
      value &&
      typeof value === 'object' &&
      'start' in value &&
      'end' in value
    ) {
      const rangeValue = value as Record<string, number>;
      if (key === 'sel') {
        sel.from = rangeValue.start.toString();
        sel.to = rangeValue.end.toString();
      } else if (key === 'maxLevel') {
        maxLevel.from = rangeValue.start.toString();
        maxLevel.to = rangeValue.end.toString();
      } else if (key === 'duration') {
        duration.from = rangeValue.start.toString();
        duration.to = rangeValue.end.toString();
      }
    } else {
      const items = value as string[] | number[];
      tableFilters[key] = items.map((category: string | number) => ({
        key: category,
        label: category,
        icon: undefined,
        className: '',
      }));
    }
  });

  return {
    time,
    duration,
    sel,
    maxLevel,
    tableFilters,
  };
};

export const tableFiltersToNoiseEventFilter = (
  tableFilters: IFilterTypes,
  time: IRangeFilters
): {
  filter: Partial<NoiseEventSummaryFilter>;
} => {
  const result: {
    locationNames?: string[];
    aircraftCategories?: AircraftCategory[];
    operatorCategories?: OperatorCategory[];
    classification?: string[];
    operationIds?: number[];
    limitToIds?: number[];
    airlines?: string[];
    aircraftTypes?: string[];
    timePeriod?: { start: number; end: number };
  } = {};

  if (tableFilters.locationNames && tableFilters.locationNames.length > 0) {
    result.locationNames = tableFilters.locationNames.map(item => item.key);
  }

  if (tableFilters.aircraftCategories.length > 0) {
    result.aircraftCategories = tableFilters.aircraftCategories.map(
      item => item.key as AircraftCategory
    );
  }

  if (tableFilters.operatorCategories.length > 0) {
    result.operatorCategories = tableFilters.operatorCategories.map(
      item => item.key as OperatorCategory
    );
  }

  if (tableFilters.classifications.length > 0) {
    result.classification = tableFilters.classifications.map(item =>
      item.key === 'NotSet' ? 'Empty' : item.key
    );
  }

  if (tableFilters.operationIds && tableFilters.operationIds.length > 0) {
    result.operationIds = tableFilters.operationIds.map(item => Number(item.key));
  }

  if (tableFilters.limitToIds && tableFilters.limitToIds.length > 0) {
    result.limitToIds = tableFilters.limitToIds.map(item => Number(item.key));
  }

  if (tableFilters.airlines && tableFilters.airlines.length > 0) {
    result.airlines = tableFilters.airlines.map(item => item.key);
  }

  if (tableFilters.aircraftTypes && tableFilters.aircraftTypes.length > 0) {
    result.aircraftTypes = tableFilters.aircraftTypes.map(item => item.key);
  }

  if (time.from && time.to) {
    result.timePeriod = {
      start: convertTimeStringToSeconds(time.from),
      end: convertTimeStringToSeconds(time.to),
    };
  }

  return {
    filter: result,
  };
};
